import { Link } from "react-router-dom";
import { Clock, Image, MapPin, Phone, ShieldCheck } from "lucide-react";
import { CLINIC } from "@/data/clinic";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { LogoMark } from "@/components/Logo";

const TITLE = "About Us | Agastya Chiro and Ancient Marma Therapy, Kurnool";
const DESC =
    "About Agastya Chiro and Ancient Marma Therapy — an independent traditional wellness centre in Nagi Reddy Revenue Colony, Kurnool, offering Marma Chikitsa and complementary therapies.";

export default function AboutPage() {
    return (
        <div className="bg-cream text-ink" data-testid="about-page">
            <Seo title={TITLE} description={DESC} path="/about" />
            <Header />
            <main className="mx-auto max-w-4xl px-4 pb-24 pt-36 sm:px-6 md:pt-44">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">About Us</p>
                    <h1
                        className="mt-4 font-serif text-4xl font-medium leading-[1.05] tracking-tight md:text-5xl"
                        data-testid="about-page-heading"
                    >
                        A Quiet Wellness Centre in the Heart of Kurnool
                    </h1>
                </Reveal>

                <article className="mt-12 space-y-12">
                    <Reveal>
                        <section>
                            <h2 className="font-serif text-2xl font-medium text-forest md:text-3xl">Who we are</h2>
                            <p className="mt-4 text-sm leading-relaxed text-moss md:text-base">
                                Agastya Chiro and Ancient Marma Therapy is an independent wellness centre in
                                Nagi Reddy Revenue Colony, near Vastralaya Mall, Kurnool, Andhra Pradesh. We
                                practise Ancient Marma Chikitsa — the classical art of working with the
                                body's vital points — alongside hands-on bodywork including foot, body and
                                head massage, cupping and scraping, with chiropractic care and Nadi Vaidya
                                approaches.
                            </p>
                            <p className="mt-4 text-sm leading-relaxed text-moss md:text-base">
                                We are a single, local centre — one door, one room, one unhurried way of
                                working. Every session begins with listening and is shaped around the
                                person in front of us.
                            </p>
                        </section>
                    </Reveal>

                    <Reveal>
                        <section>
                            <h2 className="font-serif text-2xl font-medium text-forest md:text-3xl">
                                How we communicate
                            </h2>
                            <p className="mt-4 text-sm leading-relaxed text-moss md:text-base">
                                Traditional practices deserve honest presentation. We describe Marma
                                Chikitsa as what it is: a complementary wellness practice from classical
                                Ayurvedic tradition. We do not promise cures, guaranteed outcomes or medical
                                results, and we will always encourage you to keep your physician involved in
                                any health concern. You can read more in our{" "}
                                <Link
                                    to="/medical-disclaimer"
                                    className="font-semibold text-forest underline decoration-terracotta/50 underline-offset-4 transition-colors duration-300 hover:text-terracotta"
                                >
                                    medical disclaimer
                                </Link>
                                .
                            </p>
                            <div className="mt-6 flex gap-4 rounded-2xl border border-sand bg-bone p-6">
                                <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-forest" />
                                <p className="text-sm leading-relaxed text-moss">
                                    <span className="font-semibold text-ink">Our promise:</span> gentle,
                                    non-invasive sessions; clear information before you book; and respect
                                    for your existing medical care — always.
                                </p>
                            </div>
                        </section>
                    </Reveal>

                    <Reveal>
                        <section>
                            <h2 className="font-serif text-2xl font-medium text-forest md:text-3xl">
                                Visit us
                            </h2>
                            <p className="mt-4 flex items-start gap-2 text-sm leading-relaxed text-moss md:text-base">
                                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
                                {CLINIC.addressLines.join(" ")}
                            </p>
                            <p className="mt-3 flex items-center gap-2 text-sm text-moss">
                                <Clock className="h-4 w-4 shrink-0 text-terracotta" />
                                {CLINIC.hours.weekdays} • {CLINIC.hours.sunday}
                            </p>
                            <p className="mt-3 flex items-center gap-2 text-sm text-moss">
                                <Phone className="h-4 w-4 shrink-0 text-terracotta" />
                                <a href={CLINIC.phoneTel} className="font-semibold text-forest" data-testid="about-page-phone">
                                    {CLINIC.phoneDisplay}
                                </a>
                            </p>
                        </section>
                    </Reveal>

                    <Reveal>
                        <section>
                            <h2 className="font-serif text-2xl font-medium text-forest md:text-3xl">
                                Meet the Practitioner
                            </h2>
                            <div
                                className="mt-6 rounded-3xl border border-sand bg-bone p-8 md:p-10"
                                data-testid="practitioner-bio"
                            >
                                <div className="flex items-center gap-4">
                                    <LogoMark className="h-14 w-14" />
                                    <div>
                                        <p className="font-serif text-xl font-medium leading-snug text-ink">
                                            Marma, Chiro & Nadi Vaidya Practitioner
                                        </p>
                                        <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-terracotta">
                                            8+ years of hands-on practice
                                        </p>
                                    </div>
                                </div>
                                <p className="mt-6 text-sm leading-relaxed text-moss md:text-base">
                                    With over eight years of experience, our practitioner specializes in
                                    Marma Chikitsa, chiropractic care and Nadi Vaidya, with a focus on
                                    personalized care for people experiencing musculoskeletal discomfort
                                    and mobility-related concerns.
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-moss md:text-base">
                                    Over the years, the practice has worked with individuals experiencing
                                    concerns such as frozen shoulder, cervical spondylosis, sciatica, knee
                                    pain and joint pain, using traditional, hands-on approaches tailored to
                                    each individual's needs. The approach combines traditional marma
                                    knowledge, hands-on therapeutic techniques and individualized
                                    assessment to support mobility, comfort, relaxation and overall
                                    wellbeing.
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-moss md:text-base">
                                    Every session begins with understanding the individual's concerns and
                                    health background, followed by a personalized approach designed around
                                    their comfort and needs.
                                </p>
                                <blockquote className="mt-7 border-l-2 border-gold pl-5 font-serif text-lg italic leading-relaxed text-ink md:text-xl">
                                    "My aim is to combine traditional healing knowledge with personalized
                                    care, helping each individual move, relax, and feel better in a safe
                                    and supportive environment."
                                </blockquote>
                                <p className="mt-6 text-xs leading-relaxed text-moss/80">
                                    This work is offered as complementary wellness care. It supports comfort
                                    and mobility but does not diagnose, treat or cure medical conditions —
                                    please continue to consult your physician for any health concern.
                                </p>
                            </div>
                        </section>
                    </Reveal>

                    <Reveal>
                        <div
                            data-testid="placeholder-photos"
                            className="rounded-2xl border border-dashed border-terracotta/60 bg-cream/60 p-5"
                        >
                            <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-terracotta">
                                <Image className="h-3.5 w-3.5" /> Editable placeholder
                            </p>
                            <p className="mt-2 text-sm font-semibold text-ink">Clinic & practitioner photographs</p>
                            <p className="mt-1 text-xs leading-relaxed text-moss">
                                Real photos of the centre and practitioner will be added here and across
                                the website once provided.
                            </p>
                        </div>
                    </Reveal>
                </article>
            </main>
            <Footer />
        </div>
    );
}
